/* eslint-disable require-jsdoc */
import * as env from '../../utils/environment';
import { isFunction, isUndefined } from '../../utils/utils';
import { IS_SAFARI, IS_FIREFOX } from '../../utils/environment';
import loggerManager from '../../utils/log/logger-manager';
import generateUUID from '../../utils/uuid';

interface GetDisplayMediaOption {
  audio: boolean,
  systemAudio: boolean,
  width: number,
  height: number,
  frameRate: number,
  echoCancellation?: boolean,
  autoGainControl?: boolean,
  noiseSuppression?: boolean,
  captureElement?: HTMLElement,
  preferDisplaySurface?: 'current-tab' | 'tab' | 'window' | 'monitor',
  selfBrowserSurface?: 'include' | 'exclude',
}

export const currentTabID = generateUUID();
if (typeof navigator !== 'undefined' && navigator.mediaDevices && 'setCaptureHandleConfig' in navigator.mediaDevices) {
  try {
    // @ts-ignore
    navigator.mediaDevices.setCaptureHandleConfig({
      handle: currentTabID,
      exposeOrigin: true,
      permittedOrigins: ['*']
    });
  } catch (error) {}
}
const getDisplayMedia = async function (options: GetDisplayMediaOption) {
  let audioStream = null;
  const screenConstraints = buildScreenConstraints(options);
  loggerManager.info(`getDisplayMedia with constraints: ${JSON.stringify(screenConstraints)}`);
  const captureStream = await navigator.mediaDevices.getDisplayMedia(screenConstraints);
  if (options.systemAudio && captureStream.getAudioTracks().length === 0 &&
    ((env.IS_CHROME && env.CHROME_MAJOR_VERSION < 74) || IS_SAFARI || IS_FIREFOX)) {
    loggerManager.warn('Your browser not support capture system audio');
  }
  // Chrome 采集屏幕分享会根据屏幕是否运动自动降采集帧率，此调用可以限制最低的采集帧率。
  // 此处将最低采集帧率设置为目标采集帧率，确保采集不掉帧。之前挺多客户反馈屏幕分享掉帧的问题。
  // 无法直接在 getDisplayMedia 中设置 min，详见下述链接。
  // ref: https://bugs.chromium.org/p/chromium/issues/detail?id=1463927
  const videoTrack = captureStream.getVideoTracks()[0];
  if (videoTrack) {
    if (options.frameRate) {
      try {
        await videoTrack
          .applyConstraints({ frameRate: { min: options.frameRate, ideal: options.frameRate }, width: options.width, height: options.height });
      } catch (error) {
        loggerManager.warn(`screen applyConstraints failed: ${error}`);
      }
    }

    if (options.captureElement) {
      await cropCurrentTabVideoTrackTo(videoTrack, options.captureElement);
    }
  }
  if (options.audio) {
    const constraints = buildConstraints(options);
    loggerManager.info(`getUserMedia with constraints: ${JSON.stringify(constraints)}`);
    audioStream = await navigator.mediaDevices.getUserMedia(constraints);
    captureStream.addTrack(audioStream.getAudioTracks()[0]);
  }
  return captureStream;
};

// 区域采集
async function cropCurrentTabVideoTrackTo(videoTrack: MediaStreamTrack, captureElement: HTMLElement) {
  // @ts-ignore
  if ('CropTarget' in window && 'fromElement' in CropTarget && isFunction(videoTrack.cropTo)) {
    try {
      // @ts-ignore
      const isCurrentTab = videoTrack.getCaptureHandle()?.handle === currentTabID;
      if (!isCurrentTab) return;
      // @ts-ignore
      const cropTarget = await CropTarget.fromElement(captureElement);
      // @ts-ignore
      await videoTrack.cropTo(cropTarget);
    } catch (error) {
      loggerManager.warn(`cropTo target failed ${error}`);
    }
  }

  // TODO: element capture, Chrome 121+ 支持，目前测试 Chrome canary 有问题，后续再支持
  // Region Capture 会受到其他 z-index 更高的元素遮挡，而 Element Capture 不会
  // https://developer.chrome.com/docs/web-platform/element-capture
  // if (options.captureElement) {
  //   if ('RestrictionTarget' in window && 'fromElement' in RestrictionTarget && isFunction(videoTrack.restrictTo)) {
  //     const restrictionTarget = await RestrictionTarget.fromElement(options.captureElement);
  //     await videoTrack.restrictTo(restrictionTarget);
  //   }
  // }
}

function buildConstraints(options: any) {
  const audioConstraints: MediaTrackConstraints = {
    echoCancellation: options.echoCancellation,
    autoGainControl: options.autoGainControl,
    noiseSuppression: options.noiseSuppression,
    sampleRate: options.sampleRate,
    channelCount: options.channelCount
  };
  if (!isUndefined(options.microphoneId)) {
    audioConstraints.deviceId = options.microphoneId;
  }
  return { audio: audioConstraints, video: false };
}

function buildScreenConstraints(options: GetDisplayMediaOption) {
  const screenConstraints: MediaStreamConstraints = {
    preferCurrentTab: options.preferDisplaySurface === 'current-tab' || !!options.captureElement,
    // 如下三个参数参考：https://developer.chrome.com/docs/web-platform/screen-sharing-controls/
    // 分享屏幕时，提供分享系统音频选项按钮。Windows 中生效。
    // @ts-ignore
    systemAudio: 'include',
    // 分享选择框，Tab 选择页中包括当前 tab 页面
    selfBrowserSurface: options.selfBrowserSurface || 'include',
    // 允许分享 tab 页后，用户可动态切换分享的 tab 页
    surfaceSwitching: 'include'
  };
  const videoConstraints = {
    width: IS_SAFARI ? { max: options.width } : { ideal: options.width, max: options.width },
    height: IS_SAFARI ? { max: options.height } : { ideal: options.height, max: options.height },
    frameRate: options.frameRate,
    // Mac Chrome 分享选项框默认顺序被改为了 Tabs / Windows / Monitors，此处显式声明为默认显示 monitor，和以前的浏览器版本的行为保持一致。
    displaySurface: options.preferDisplaySurface || 'monitor'
  };
  screenConstraints.video = videoConstraints;
  if (options.systemAudio) {
    const { echoCancellation = true, noiseSuppression = false, autoGainControl = false } = options;
    screenConstraints.audio = {
      echoCancellation,
      noiseSuppression,
      autoGainControl,
      sampleRate: 48000
    };
  }
  return screenConstraints;
}

export default getDisplayMedia;
